import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import "../admin/pages/styles/LightSwitch.css";
import ArrowDown from "/assets/ArrowDown.svg";
import Delete from "/Delete1.png?url";
import axios from "axios";
import { Bar } from "react-chartjs-2";

function EditPoleModal({ pole, onClose, fetchSubordinatePoles }) {
  const navigate = useNavigate();
  const [editedPole, setEditedPole] = useState({
    ...pole,
    screenInstall: formatDate(pole?.screenInstall), // Apply formatting when initializing state
  });

  const [timeInputs, setTimeInputs] = useState(
    Array.isArray(pole.schedule_light_bulbs)
      ? pole.schedule_light_bulbs.map((bulb, index) => ({
          ...bulb,
          id: index + Date.now(), // Unique ID
        }))
      : []
  ); // Ensure it's an array with unique IDs

  const [hasScreen, setHasScreen] = useState(pole?.hasScreen || false);

  const [mainPoleData, setMainPoleData] = useState({
    poleId: pole?.poleId || "",
    poleCode: pole?.mainPoleCode || "",
    poleName: pole?.mainPoleName || "", // Ensure this uses the correct name
    deviceCode: pole?.deviceCode || "",
    firmwareVersion: pole?.firmwareVersion || "",
    installationDate: pole?.installationDate || "",
    poleLocation: pole?.mainPoleLocation || "",
    latitude: pole?.mainPoleLat || "",
    longitude: pole?.mainPoleLng || "",
    cameras: Array.isArray(pole?.cameras) ? pole.cameras : [],
    branchName: pole?.branchName || "",
    hasScreen: pole?.hasScreen || "",
    screenId: pole?.screenId || "",
    screenModel: pole?.screenModel || "",
    screenInstall: pole?.screenInstall || "",
    lightActive: pole?.lightActive || "",
    master_id: pole?.master_id || "",
    master_name: pole?.master_name || "",
    mainPoleActive: pole?.mainPoleActive || "",
    custom_schedule_light_bulbs_active:
      pole?.custom_schedule_light_bulbs_active || false,
    schedule_light_bulbs_rule: pole?.schedule_light_bulbs_rule || 1,
  });

  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [selectedMasterId, setSelectedMasterId] = useState("");

  const handleSave = async () => {
    if (!mainPoleData.poleId) {
      alert("No pole ID is provided. Update cannot be performed.");
      return;
    }

    const installationDate = formatDate(mainPoleData.installationDate);
    const screenInstallationDate = convertDateToISO(editedPole.screenInstall);

    const payload = {
      // ข้อมูลทั่วไปของเสา
      code: mainPoleData.poleCode,
      hardware_id: mainPoleData.deviceCode,
      name: mainPoleData.poleName,
      location: mainPoleData.poleLocation,
      lat: mainPoleData.latitude,
      lng: mainPoleData.longitude,
      last_dim: mainPoleData.lastDim,
      active: !!mainPoleData.mainPoleActive,
      installation_date: installationDate ? installationDate : null,
      firmware_version: mainPoleData.firmwareVersion,
      // ข้อมูลจอ LED
      has_screen: hasScreen,
      screen_id: editedPole.screenId,
      screen_model: editedPole.screenModel,
      screen_installation_date: screenInstallationDate,
      // กฎการควบคุมแสง
      schedule_light_bulbs_rule: mainPoleData.schedule_light_bulbs_rule,
      custom_schedule_light_bulbs_active:
        mainPoleData.custom_schedule_light_bulbs_active,
      latest_schedule_light_bulbs_period:
        mainPoleData.latest_schedule_light_bulbs_period,
      latest_schedule_light_bulbs_brightness:
        mainPoleData.latest_schedule_light_bulbs_brightness,
      latest_schedule_light_bulbs_on:
        mainPoleData.latest_schedule_light_bulbs_on,
      // Schedule light bulbs
      schedule_light_bulbs: timeInputs,
      // Conditionally add branch_id or master_id
      ...(editedPole.poleType === "main" &&
        selectedBranchId && { branch_id: selectedBranchId }),
      ...(editedPole.poleType !== "main" &&
        selectedMasterId && { master_id: selectedMasterId }),
      // ข้อมูลของกล้อง
      cameras: mainPoleData.cameras.map((camera) => ({
        id: camera.id,
        name: camera.name,
        model: camera.model,
        is_broadcast: camera.cameraActive,
        deleted_on: camera.deleted_on,
      })),
    };

    try {
      const response = await axios.put(
        `${import.meta.env.VITE_API_URL}/update-polemaster/${mainPoleData.poleId}`,
        payload
      );

      onClose();
      if (editedPole.poleType === "main") {
        navigate("/lightswitch");
      } else {
        fetchSubordinatePoles();
      }
      alert("Update successful!");
    } catch (error) {
      alert(
        "Update failed: " +
          (error.response && error.response.data
            ? error.response.data.error
            : error.message)
      );
    }
    console.log(payload);
  };

  const poleType = editedPole.poleType === "main" ? "เสาหลัก" : "เสารอง";

  const renderFloatLabelInput = (
    label,
    type,
    name,
    id,
    value,
    onChange,
    required = true
  ) => (
    <div className="float-label-container">
      <input
        type={type}
        id={id}
        name={name}
        value={value}
        onChange={onChange}
        className="input-required"
        required={required}
        placeholder=" "
      />
      <label htmlFor={id}>
        {label} {required && <span className="required-asterisk"> *</span>}
      </label>
    </div>
  );

  const handleMainPoleChange = (fieldName, value) => {
    if (fieldName === "installationDate") {
      const formattedDate = formatDate(value);

      setMainPoleData((prevData) => ({
        ...prevData,
        installationDate: formattedDate,
      }));
    } else {
      setMainPoleData((prevData) => ({
        ...prevData,
        [fieldName]: value,
      }));
    }
  };

  const handleAddTimeClick = (event) => {
    event.preventDefault();
    const newInput = {
      id: Date.now(), // Unique ID
      period: "", // Default period
      brightness: 0, // Default brightness
    };
    setTimeInputs((prevInputs) =>
      Array.isArray(prevInputs) ? prevInputs.concat(newInput) : [newInput]
    );
  };

  const handleTimeChange = (id, newTimeValue) => {
    setTimeInputs((prevInputs) =>
      prevInputs.map((input) =>
        input.id === id ? { ...input, period: newTimeValue } : input
      )
    );
  };

  const handleRangeChange = (id, newValue) => {
    setTimeInputs((prevInputs) =>
      prevInputs.map((input) =>
        input.id === id ? { ...input, brightness: newValue } : input
      )
    );
  };

  const handleRemoveTimeInput = (idToRemove) => {
    setTimeInputs((prevInputs) =>
      prevInputs.filter((input) => input.id !== idToRemove)
    );
  };

  const handleCCTVChange = (index, field, value) => {
    const updatedCameras = mainPoleData.cameras.map((camera, i) =>
      i === index ? { ...camera, [field]: value } : camera
    );
    setMainPoleData((prevData) => ({
      ...prevData,
      cameras: updatedCameras,
    }));
  };

  const handleToggleCCTVActive = (index) => {
    const updatedCameras = mainPoleData.cameras.map((cctv, i) =>
      i === index ? { ...cctv, cameraActive: !cctv.cameraActive } : cctv
    );
    setMainPoleData((prevData) => ({
      ...prevData,
      cameras: updatedCameras,
    }));
  };

  const handleDeleteCCTV = (index) => {
    const now = new Date().toISOString();

    const updatedCameras = mainPoleData.cameras.map((camera, i) =>
      i === index ? { ...camera, deleted_on: now, isHidden: true } : camera
    );

    setMainPoleData((prevData) => ({
      ...prevData,
      cameras: updatedCameras,
    }));
  };

  const handleDeleteLED = () => {
    setEditedPole({
      ...editedPole,
      screenId: null,
      screenModel: null,
      screenInstall: null,
    });
    setHasScreen(false);
  };

  function timeToMinutes(timeValue) {
    if (!timeValue) {
      return Infinity;
    }

    const [hours, minutes] = timeValue.split(":").map(Number);
    return hours * 60 + minutes;
  }

  const sortedTimeInputs = Array.isArray(timeInputs)
    ? [...timeInputs].sort((a, b) => {
        const timeA = a.period ? timeToMinutes(a.period) : Infinity;
        const timeB = b.period ? timeToMinutes(b.period) : Infinity;

        return timeA - timeB;
      })
    : [];

  function minutesToTime(minutes) {
    if (minutes < 0) {
      minutes = 23 * 60 + 59;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, "0")}:${mins
      .toString()
      .padStart(2, "0")}`;
  }

  const labels = sortedTimeInputs.map((input, index) => {
    if (index < sortedTimeInputs.length - 1) {
      const nextTimeInMinutes =
        timeToMinutes(sortedTimeInputs[index + 1].period) - 1;
      const adjustedNextTime = minutesToTime(nextTimeInMinutes);
      return `${input.period} - ${adjustedNextTime}`;
    } else {
      const firstTimeInMinutes = timeToMinutes(sortedTimeInputs[0].period) - 1;
      const adjustedFirstTime = minutesToTime(firstTimeInMinutes);
      return `${input.period} - ${adjustedFirstTime}`;
    }
  });

  const chartData = {
    labels,
    datasets: [
      {
        data: sortedTimeInputs.map((input) => input.brightness),
        backgroundColor: "#F5B81C",
      },
    ],
  };

  const options = {
    scales: {
      x: {
        ticks: {
          color: "white",
        },
        grid: {
          color: "transparent",
        },
      },
      y: {
        position: "right",
        beginAtZero: true,
        ticks: {
          color: "white",
          callback: function (value) {
            return value + "%";
          },
        },
        min: 0,
        max: 100,
        grid: {
          color: "rgba(255, 255, 255, 0.1)",
        },
      },
    },
    plugins: {
      legend: {
        display: false,
      },
    },
    maintainAspectRatio: false,
    responsive: true,
  };

  function formatDate(dateString) {
    if (!dateString) return null;
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const buddhistYear = date.getFullYear();
    return `${buddhistYear}-${month}-${day}`;
  }

  function convertDateToISO(date) {
    if (!date) return null;
    const d = new Date(date);
    let month = "" + (d.getMonth() + 1);
    let day = "" + d.getDate();
    let year = d.getFullYear();

    if (month.length < 2) month = "0" + month;
    if (day.length < 2) day = "0" + day;

    return [year, month, day].join("-");
  }

  const [mainPoles, setMainPoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedPoleName, setSelectedPoleName] = useState("");

  const [branches, setBranches] = useState([]);
  const [branchesLoading, setBranchesLoading] = useState(true);
  const [branchesError, setBranchesError] = useState(null);
  const [selectedBranchName, setSelectedBranchName] = useState(
    mainPoleData.branchName
  );

  useEffect(() => {
    setSelectedBranchName(mainPoleData.branchName);
  }, [mainPoleData.branchName]);

  useEffect(() => {
    if (editedPole.poleType === "main") {
      setBranchesLoading(true);
      axios
        .get(`${import.meta.env.VITE_API_URL}/api/branches`)
        .then((response) => {
          setBranches(response.data);
          setBranchesLoading(false);
        })
        .catch((error) => {
          setBranchesError(error.message);
          setBranchesLoading(false);
        });
    }
  }, [editedPole.poleType]);

  const handleSelectLightClick = async () => {
    if (!isOpen && !mainPoles.length && editedPole.poleType !== "main") {
      setLoading(true);
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/allMainPoles`);
        setMainPoles(response.data.mainPoles);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching main poles:", error);
        setLoading(false);
      }
    }
    setIsOpen(!isOpen);
  };

  const handleSelectPole = (poleId, poleName) => {
    if (editedPole.poleType === "main") {
      setSelectedBranchName(poleName);
      setSelectedBranchId(poleId);
    } else {
      setSelectedPoleName(poleName);
      setSelectedMasterId(poleId);
    }
    setIsOpen(false);
  };

  const handleScreenChange = (field, value) => {
    setEditedPole((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  return (
    <div className="BranchModal">
      <div className="add-user-modal">
        <h3>แก้ไข{poleType}</h3>
        <form className="addlight">
          <label>เพิ่มข้อมูล{poleType}</label>

          <div
            className="selectLight"
            style={{ border: "2px solid #2762f8" }}
            onClick={handleSelectLightClick}
          >
            <span>
              {editedPole.poleType === "main"
                ? selectedBranchName || mainPoleData.branchName
                : selectedPoleName || mainPoleData.master_name}
            </span>

            <span className="onTop">
              {editedPole.poleType === "main" ? "เลือกสาขา" : "เลือกเสาหลัก"}
              <span className="required-asterisk"> *</span>
            </span>

            <img
              className={`arrow-icon-branch ${isOpen ? "rotated" : ""}`}
              src={ArrowDown}
              alt="Arrow"
            />
            {isOpen && (
              <div className="dropdown-content-branchAll">
                <div className="notBlur">
                  {loading ? (
                    <p>Loading...</p>
                  ) : (
                    <ul>
                      {editedPole.poleType === "main"
                        ? branches.map((branch, index) => (
                            <li
                              key={`${branch.id}-${index}`}
                              onClick={() =>
                                handleSelectPole(branch.id, branch.name)
                              }
                            >
                              {branch.name}
                            </li>
                          ))
                        : mainPoles.map((pole, index) => (
                            <li
                              key={`${pole.id}-${index}`}
                              onClick={() =>
                                handleSelectPole(pole.id, pole.name)
                              }
                            >
                              {pole.name}
                            </li>
                          ))}
                    </ul>
                  )}
                </div>
              </div>
            )}
          </div>

          {renderFloatLabelInput(
            "รหัสเสา ",
            "text",
            "poleCode",
            "poleCode",
            mainPoleData.poleCode,
            (e) => handleMainPoleChange("poleCode", e.target.value)
          )}
          {renderFloatLabelInput(
            "ชื่อเสา ",
            "text",
            "poleName",
            "poleName",
            mainPoleData.poleName,
            (e) => handleMainPoleChange("poleName", e.target.value)
          )}

          {renderFloatLabelInput(
            "รหัสอุปกรณ์ ",
            "text",
            "deviceCode",
            "deviceCode",
            mainPoleData.deviceCode,
            (e) => handleMainPoleChange("deviceCode", e.target.value)
          )}
          {renderFloatLabelInput(
            "เวอร์ชั่น Firmware ",
            "text",
            "firmwareVersion",
            "firmwareVersion",
            mainPoleData.firmwareVersion,
            (e) => handleMainPoleChange("firmwareVersion", e.target.value)
          )}

          {renderFloatLabelInput(
            "วันที่ติดตั้งเสา",
            "date",
            "installationDate",
            "installationDate",
            formatDate(mainPoleData.installationDate),
            (e) => handleMainPoleChange("installationDate", e.target.value)
          )}

          {renderFloatLabelInput(
            "สถานที่ติดตั้งเสา",
            "text",
            "poleLocation",
            "poleLocation",
            mainPoleData.poleLocation,
            (e) => handleMainPoleChange("poleLocation", e.target.value)
          )}
          {renderFloatLabelInput(
            "ละติจูด",
            "text",
            "latitude",
            "latitude",
            mainPoleData.latitude,
            (e) => handleMainPoleChange("latitude", e.target.value)
          )}
          {renderFloatLabelInput(
            "ลองจิจูด",
            "text",
            "longitude",
            "longitude",
            mainPoleData.longitude,
            (e) => handleMainPoleChange("longitude", e.target.value)
          )}

          <div className="toggle-switch-container">
            <span>เปิด/ปิดการใช้งานเสา</span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={mainPoleData.mainPoleActive}
                onChange={(e) =>
                  setMainPoleData((prev) => ({
                    ...prev,
                    mainPoleActive: e.target.checked,
                  }))
                }
              />
              <span className="slider"></span>
            </label>
          </div>

          <label>ปรับความสว่างไฟแบบอัตโนมัติ</label>
          <div className="toggle-switch-container">
            <span>
              {mainPoleData.schedule_light_bulbs_rule === 1
                ? "ค่าเริ่มต้น"
                : "กำหนดเอง"}
            </span>
            <label className="toggle-switch">
              <input
                type="checkbox"
                checked={mainPoleData.schedule_light_bulbs_rule === 2}
                onChange={(e) => {
                  const newRule = e.target.checked ? 2 : 1;
                  setMainPoleData((prev) => ({
                    ...prev,
                    schedule_light_bulbs_rule: newRule,
                  }));
                }}
              />
              <span className="slider"></span>
            </label>
          </div>

          {mainPoleData.schedule_light_bulbs_rule === 2 && (
            <>
              <div className="add-time-button-container">
                <button
                  className="add-time-button"
                  onClick={handleAddTimeClick}
                >
                  + เพิ่มเวลา
                </button>
              </div>
              <div className="toggle-switch-container">
                <span>
                  {mainPoleData.custom_schedule_light_bulbs_active
                    ? "ทำงานตามปกติ"
                    : "ปิดการทำงาน"}
                </span>
                <label className="toggle-switch">
                  <input
                    type="checkbox"
                    checked={mainPoleData.custom_schedule_light_bulbs_active}
                    onChange={(e) =>
                      setMainPoleData((prev) => ({
                        ...prev,
                        custom_schedule_light_bulbs_active: e.target.checked,
                      }))
                    }
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </>
          )}
          {mainPoleData.schedule_light_bulbs_rule === 2 &&
            Array.isArray(timeInputs) &&
            timeInputs.map((input, index) => (
              <div key={input.id} className="time-input-container">
                <label>เวลา</label>
                <input
                  type="time"
                  className="time-display"
                  value={input.period}
                  onChange={(e) => handleTimeChange(input.id, e.target.value)}
                />
                <div
                  className="slider-container"
                  style={{ position: "relative" }}
                >
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={input.brightness}
                    className="gradient-range"
                    onChange={(e) =>
                      handleRangeChange(input.id, e.target.value)
                    }
                  />
                  <div
                    className="value-display"
                    style={{
                      left: `calc(${input.brightness}% + 10px)`,
                      transform: "translateX(-50%)",
                      bottom: "-15px",
                    }}
                  >
                    {input.brightness}%
                  </div>
                </div>
                <div
                  className="delete-button"
                  onClick={() => handleRemoveTimeInput(input.id)}
                >
                  <img src={Delete} alt="Delete" />
                </div>
              </div>
            ))}
        </form>
        {mainPoleData.schedule_light_bulbs_rule === 2 &&
          Array.isArray(timeInputs) &&
          timeInputs.some((input) => input.period) && (
            <div className="setlight chartContainer">
              <Bar data={chartData} options={options} />
            </div>
          )}

        <>
          <hr />
          <div className="addCctv">
  <h2>กล้อง CCTV</h2>

  {mainPoleData.cameras.length > 0 ? (
    <form className="addlight">
      {mainPoleData.cameras.map((cctv, index) => {
        const isEmpty =
          !cctv.name && !cctv.model && !cctv.installation_date;
        return (
          !isEmpty && (
            <div key={`${cctv.id}-${index}`}>
              {renderFloatLabelInput(
                "ชื่อกล้อง",
                "text",
                `name-${index}`,
                `name-${index}`,
                cctv.name || "",
                (e) =>
                  handleCCTVChange(
                    index,
                    "name",
                    e.target.value
                  )
              )}
              {renderFloatLabelInput(
                "รุ่นกล้อง",
                "text",
                `model-${index}`,
                `model-${index}`,
                cctv.model || "",
                (e) =>
                  handleCCTVChange(
                    index,
                    "model",
                    e.target.value
                  )
              )}
              {renderFloatLabelInput(
                "วันที่ติดตั้ง",
                "date",
                `installationDate-${index}`,
                `installationDate-${index}`,
                cctv.installation_date || "",
                (e) =>
                  handleCCTVChange(
                    index,
                    "installationDate",
                    e.target.value
                  )
              )}

              <div className="flexAddCCtv">
                <div className="toggle-switch-container">
                  <span>เปิดใช้งานกล้อง</span>
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={cctv.cameraActive}
                      onChange={() =>
                        handleToggleCCTVActive(index)
                      }
                    />
                    <span className="slider"></span>
                  </label>
                </div>
                <div
                  className="delete-button-cctv"
                  onClick={(e) => {
                    e.preventDefault();
                    handleDeleteCCTV(index);
                  }}
                >
                  <img src={Delete} alt="Delete" />
                </div>
              </div>
            </div>
          )
        );
      })}
    </form>
  ) : (
    <form className="addlight">
      <div>ไม่มีข้อมูลกล้อง CCTV ในระบบ</div>
    </form>
  )}
</div>


          <hr />
          <div>
            {editedPole.poleType === "main" && (
              <div className="addLed">
                <h2>จอ LED</h2>
                {editedPole.screenId &&
                editedPole.screenModel &&
                editedPole.screenInstall ? (
                  <form className="addlight">
                    <div className="led-item">
                      {renderFloatLabelInput(
                        "รหัสจอ LED",
                        "text",
                        "screenId",
                        "screenId",
                        editedPole.screenId,
                        (e) => handleScreenChange("screenId", e.target.value)
                      )}
                      {renderFloatLabelInput(
                        "รุ่นจอ",
                        "text",
                        "screenModel",
                        "screenModel",
                        editedPole.screenModel,
                        (e) => handleScreenChange("screenModel", e.target.value)
                      )}
                      {renderFloatLabelInput(
                        "วันติดตั้ง",
                        "date",
                        "screenInstall",
                        "screenInstall",
                        editedPole.screenInstall,
                        (e) =>
                          handleScreenChange("screenInstall", e.target.value)
                      )}
                      <div className="flexAddCCtv">
                        <div className="toggle-switch-container">
                          <span>เปิดใช้งานจอ</span>
                          <label className="toggle-switch">
                            <input
                              type="checkbox"
                              checked={hasScreen}
                              onChange={(e) => setHasScreen(e.target.checked)}
                            />
                            <span className="slider"></span>
                          </label>
                        </div>
                        <div
                          className="delete-button-cctv"
                          onClick={handleDeleteLED}
                        >
                          <img src={Delete} alt="Delete" />
                        </div>
                      </div>
                    </div>
                  </form>
                ) : (
                  <form className="addlight">
                    <p>ไม่มีจอ LED</p>
                  </form>
                )}
              </div>
            )}
          </div>

          <div className="button-adduser-pole">
            <button className="cancel-button" onClick={onClose}>
              ยกเลิก
            </button>
            <button className="confirm-button" onClick={handleSave}>
              บันทึก
            </button>
          </div>
        </>
        <button className="close-button" onClick={onClose}>
          &times;
        </button>
      </div>
    </div>
  );
}

export default EditPoleModal;
